import { Link } from 'wouter';
import { Logo } from '../ui/logo';
import { ArrowRight, Send, Linkedin, Twitter, ExternalLink, BookOpen } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-black via-black to-gray-900 text-white py-16 relative overflow-hidden">
      {/* Gradient orbs */}
      <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-primary/20 blur-3xl opacity-30"></div>
      <div className="absolute -bottom-32 -left-32 w-64 h-64 rounded-full bg-primary/20 blur-3xl opacity-30"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-6">
          <div className="sm:col-span-2 lg:col-span-6">
            <Link href="/" className="inline-block mb-6">
              <Logo className="h-12 w-auto" />
            </Link>
            <p className="text-gray-300 mb-6 max-w-sm">
              The AI advantage wont wait.<br />Neither should you.
            </p>

            {/* Newsletter link */}
            <div className="mt-8">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Send size={14} />
                Newsletter
              </h3>
              <a
                id="footer-subscribe"
                href="https://the-context-window.ghost.io/subscribe/"
                target="_blank"
                rel="noopener noreferrer"
                className="subscribe-button inline-flex items-center text-gray-300 hover:text-white transition-colors duration-300 group"
              >
                <span className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ArrowRight size={12} />
                </span>
                Subscribe to our newsletter
              </a>
              <p className="text-xs text-gray-400 mt-2">Stay updated with our latest insights.</p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-bold mb-4 text-primary uppercase tracking-wider text-sm">
              Navigate
            </h4>
            <ul className="space-y-2.5">
              <FooterLink href="/" label="Home" />
              <FooterLink href="/ai-agents" label="AI Agents" />
              <li className="ml-4">
                <FooterLink href="/agent-hosting" label="Agent Hosting" />
              </li>
              <FooterLink href="/resources" label="Resources" />
              <FooterLink href="/about" label="About Us" />
              <FooterLink href="/contact" label="Contact" />
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-bold mb-4 text-primary uppercase tracking-wider text-sm">
              Resources
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="https://generativeaiexplained.com?utm_source=chatbotlabs&utm_medium=website&utm_campaign=footer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary transition-colors duration-300 group flex items-center"
                >
                  <span className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowRight size={12} />
                  </span>
                  AI Glossary
                  <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-bold mb-4 text-primary uppercase tracking-wider text-sm">
              Services
            </h4>
            <ul className="space-y-2.5 text-gray-300">
              <li>Coming Soon!</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            <p className="text-gray-400 text-sm text-center lg:text-left">
              &copy; {currentYear} Chat Bot Labs. All rights reserved.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="flex space-x-4">
                <SocialLink href="https://www.linkedin.com/in/jdfetterly" icon={<Linkedin size={18} />} label="LinkedIn" />
                <SocialLink href="https://x.com/realJDFetterly" icon={<Twitter size={18} />} label="X (Twitter)" />
                <SocialLink href="https://the-context-window.ghost.io/" icon={<BookOpen size={18} />} label="Blog" />
              </div>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <a
                  href="#"
                  className="text-gray-300 text-sm hover:text-primary transition-colors duration-300"
                >
                  Privacy Policy
                </a>
                <a
                  href="#"
                  className="text-gray-300 text-sm hover:text-primary transition-colors duration-300"
                >
                  Terms of Service
                </a>
                <a
                  href="#"
                  className="text-gray-300 text-sm hover:text-primary transition-colors duration-300"
                >
                  Sitemap
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

interface FooterLinkProps {
  href: string;
  label: string;
}

const FooterLink = ({ href, label }: FooterLinkProps) => (
  <li>
    {href.startsWith('/') ? (
      <Link
        href={href}
        className="text-gray-300 hover:text-primary transition-colors duration-300 group flex items-center"
        onClick={() => window.scrollTo(0, 0)}
      >
        <span className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <ArrowRight size={12} />
        </span>
        {label}
      </Link>
    ) : (
      <a
        href={href}
        className="text-gray-300 hover:text-primary transition-colors duration-300 group flex items-center"
      >
        <span className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <ArrowRight size={12} />
        </span>
        {label}
      </a>
    )}
  </li>
);

interface SocialLinkProps {
  href: string;
  icon: React.ReactNode;
  /** Accessible label for the social media link */
  label: string;
}

const SocialLink = ({ href, icon, label }: SocialLinkProps) => (
  <a
    href={href}
    aria-label={label}
    className="bg-gray-800 p-2 rounded-full hover:bg-primary/20 hover:text-primary transition-all duration-300"
    target="_blank"
    rel="noopener noreferrer"
  >
    {icon}
  </a>
);

export default Footer;
